// Compact, premium product tabs (MMT-style inspired but visually distinct).
// Used on the HomePage and on every Search Results page as a sticky bar at the top.
//
// Props:
//   active        — the currently active module slug (e.g. 'hotels')
//   onSelect      — (slug) => void; if omitted, navigates to /{slug}/search
//   modulesEnabled — { hotels: true, flights: false, ... } from /services/modules
//   variant       — 'overlay' | 'solid' (overlay = transparent text-white on hero)
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Plane, Train, Bus, Car, Home, Briefcase,
  Map as MapIcon, FileText, Ship, Coins, ShieldCheck,
} from 'lucide-react';

export const PRODUCT_TABS = [
  { value: 'hotels',    label: 'Hotels',    Icon: Building2 },
  { value: 'flights',   label: 'Flights',   Icon: Plane },
  { value: 'trains',    label: 'Trains',    Icon: Train },
  { value: 'buses',     label: 'Buses',     Icon: Bus },
  { value: 'cabs',      label: 'Cabs',      Icon: Car },
  { value: 'villas',    label: 'Villas',    Icon: Home },
  { value: 'packages',  label: 'Packages',  Icon: Briefcase },
  { value: 'tours',     label: 'Tours',     Icon: MapIcon },
  { value: 'visa',      label: 'Visa',      Icon: FileText },
  { value: 'cruise',    label: 'Cruise',    Icon: Ship },
  { value: 'forex',     label: 'Forex',     Icon: Coins },
  { value: 'insurance', label: 'Insurance', Icon: ShieldCheck },
];

const ProductTabsBar = ({ active, onSelect, modulesEnabled = { hotels: true }, order, variant = 'solid', hug = false, compact = false }) => {
  const navigate = useNavigate();
  const isOverlay = variant === 'overlay';

  // Section A: Only render enabled modules. Disabled modules are hidden entirely (no space reserved).
  // Section B: justify-center balances spacing when fewer modules are enabled.
  // Admin-defined `order` (passed in by parent) determines display sequence. Fallback: PRODUCT_TABS order.
  const orderedKeys = Array.isArray(order) && order.length ? order : PRODUCT_TABS.map((t) => t.value);
  const byKey = Object.fromEntries(PRODUCT_TABS.map((t) => [t.value, t]));
  const visibleTabs = orderedKeys
    .map((k) => byKey[k])
    .filter((t) => t && modulesEnabled[t.value] === true);

  const handleClick = (t) => {
    if (onSelect) return onSelect(t.value);
    navigate(`/${t.value}/search`);
  };

  // Hug mode: container hugs content width (no max-w-7xl, no mx-auto stretching).
  // Compact mode: smaller pill heights used on results page sticky bar.
  const padY = compact ? 'py-1.5 pb-2' : 'py-2 pb-3';
  const tabMinW = compact ? 'min-w-[68px] sm:min-w-[82px]' : 'min-w-[68px] sm:min-w-[80px]';
  const iconSize = compact ? 'h-6 w-6 sm:h-7 sm:w-7' : 'h-5 w-5 sm:h-[22px] sm:w-[22px]';
  const tabPadY = compact ? 'py-1.5 sm:py-2' : 'py-2 sm:py-2.5';

  return (
    <div
      data-testid="product-tabs-bar"
      className={`${hug ? '' : 'w-full'} ${isOverlay ? '' : 'bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800'}`}
    >
      <div className={hug ? 'px-2 sm:px-4' : 'max-w-7xl mx-auto px-2 sm:px-6 lg:px-8'}>
        {/* Section C: overflow-y-visible so the active underline is not clipped. */}
        <div className={`flex flex-nowrap justify-center overflow-x-auto overflow-y-visible no-scrollbar scroll-snap-x gap-1 sm:gap-2 ${padY}`}>
          {visibleTabs.map((t) => {
            const isActive = t.value === active;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => handleClick(t)}
                data-testid={`product-tab-${t.value}`}
                className={`relative group shrink-0 scroll-snap-center flex flex-col items-center justify-center gap-1 ${tabMinW} px-2 sm:px-3 ${tabPadY} transition-all duration-200 cursor-pointer
                  ${
                    isActive
                      ? (isOverlay ? 'text-white' : 'text-red-600 dark:text-red-400')
                      : (isOverlay
                          ? 'text-white/80 hover:text-white'
                          : 'text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400')
                  }`}
              >
                <t.Icon
                  className={`${iconSize}`}
                  strokeWidth={isActive ? 2.2 : 1.8}
                  fill={isActive ? 'currentColor' : 'none'}
                  fillOpacity={isActive ? 0.15 : 0}
                />
                <span className={`${compact ? 'text-[11px] sm:text-xs' : 'text-[10px] sm:text-[11px]'} font-semibold tracking-wide whitespace-nowrap`}>{t.label}</span>
                {isActive && !isOverlay && (
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-[3px] w-10 rounded-full bg-red-500" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProductTabsBar;
